import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { AttributesEditorDataSource } from 'src/shared-app-portal/components/attributes-editor/attributesEditorDataSource';
import { BuildInfoCheckService } from 'src/app/shared/services/build-info-check.service';
import { AccessibilityService } from 'src/app/shared/services/accessibility.service';
import { MemberEvaluationService } from '../../../../../app/members/member-evaluation.service';
import { EvaluationForm } from '../models/evaluationForm';
import { MemberEvaluationDataSource } from '../models/memberEvaluationDataSource';
import { ActivatedRoute } from '@angular/router';
import {
  LocalData,
  LocalPersistenceService
} from 'src/shared-app-portal/services/local-persistence.service';

@Component({
  selector: 'app-evaluation-respond',
  templateUrl: './evaluation-respond.component.html',
  styleUrls: ['./evaluation-respond.component.scss'],
  standalone: false
})
export class EvaluationRespondComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  isSaving: boolean = false;
  dataSource: MemberEvaluationDataSource[] = [];
  isHeaderButtonVisible: boolean = false;
  stepAnnouncement: string = '';
  isPrivate: boolean = false;
  constructor(
    private route: ActivatedRoute,
    private service: MemberEvaluationService,
    private localPersistenceService: LocalPersistenceService,
    private buildInfoCheckService: BuildInfoCheckService,
    private accessibilityService: AccessibilityService
  ) {}
  get isValidEvaluation(): boolean {
    return this.dataSource.findIndex((x) => !x.isValid) == -1;
  }
  ngOnInit(): void {
    this.subscriptions.push(
      this.service.evaluation.subscribe((result) => {
        if (result) {
          this.service.setEvaluationForm();
          this.setUpEvaluation(result);
          this.setupHeaderButton();
          this.buildInfoCheckService.initBuildFileCheck(
            "Don't worry, you won't lose your progress!"
          );
        }
      }),
      this.buildInfoCheckService.onReloadPrompt.subscribe(() => {
        this.localPersistenceService.saveDataForLater(
          this.buildLocalData(),
          this.service.secureLinkId,
          false
        );
      }),
      this.service.isLoadingOrSaving.subscribe((result) => {
        this.isSaving = result;
      })
    );
  }

  onClick() {
    if (typeof window != 'undefined') {
      window.scrollTo(0, 0);
    }
    this.localPersistenceService.saveDataForLater(
      this.buildLocalData(),
      this.service.secureLinkId,
      false
    );
  }

  onStepChange(event: { selectedIndex: number }) {
    const total = this.dataSource.length;
    this.stepAnnouncement = `Evaluation ${event.selectedIndex + 1} of ${total}`;
  }

  submit() {
    if (this.isValidEvaluation && !this.isSaving) {
      this.accessibilityService.talk('Submitting...');
      this.service.setIsLoadingOrSaving(true);
      this.service.updateEvaluation(this.dataSource);
      this.localPersistenceService.deleteLocalData(this.service.secureLinkId);
    }
  }
  downloadFile(event: { fileName: string; attributeDefinitionId: string }, seatId: string) {
    let secureLinkId = this.route.parent.snapshot?.params['secureLinkId'];
    let requestUrl = `${secureLinkId}/${event.attributeDefinitionId}/${seatId}/document`;
    this.service.downloadFile(requestUrl, event.fileName);
  }
  private setUpEvaluation(evaluationForm: EvaluationForm) {
    this.isPrivate = evaluationForm.form?.isPrivate;
    const localData = this.localPersistenceService.getLocalData<LocalData<string>[]>(
      this.service.secureLinkId
    );
    evaluationForm.evaluatees.forEach((evaluatee) => {
      let attributes = evaluatee.attributes;
      if (localData) {
        let data = localData.find((x) => x.reference == evaluatee.evaluationAssignmentId);
        let loadedAttributes =
          data && data.answers
            ? LocalPersistenceService.buildFormUsingSavedAnswers(
                data.answers,
                this.service.evaluation.value.questions
              )
            : [];
        attributes = attributes.concat(loadedAttributes);
      }
      let editorDataSource = new AttributesEditorDataSource();

      editorDataSource.sortOnName = false;
      editorDataSource.load(attributes, evaluationForm.questions);
      this.dataSource.push(new MemberEvaluationDataSource(evaluatee, editorDataSource));
    });
  }
  private setupHeaderButton() {
    if (this.isHeaderButtonVisible) return;

    this.localPersistenceService.buttonSubject.next({
      label: 'Save For Later',
      action: () =>
        this.localPersistenceService.saveDataForLater(
          this.buildLocalData(),
          this.service.secureLinkId
        ),
      tooltip:
        'Your responses will be saved for 15 days. You must use the same device and browser when ' +
        "coming back. You must still submit responses once you're ready."
    });
    this.isHeaderButtonVisible = true;
  }

  private buildLocalData(): LocalData<string>[] {
    return this.dataSource.map((x) => {
      return {
        reference: x.evaluatee.evaluationAssignmentId,
        answers: this.localPersistenceService.getAnswers(x.editorDataSource)
      };
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
